import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Stack } from '@prenuvo/halo';
import { useMutationState } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { api, PatchReportOrganRequest } from '@/api';
import { AddendumProvider } from '@/core/addendum';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { ML_SERVER } from '@/shared/constant';
import { useStudyGuard } from '@/shared/guard';
import { ReportLinkKey } from '@/shared/model';
import { Loading } from '@/shared/ui';
import { generateAppRoute } from '@/shared/util';
import { useComparisonStore, useReportOrganStore, useReportValidationStore } from '@/store';

import { Header, StudyModeDialog } from '../../component';
import {
  useInitializePreferences,
  useInitializeReport,
  useSyncComparisonStoreWithRoute,
} from '../../hook';
import { LazyComparisonSidePanel } from './comparison-side-panel';
import { KeyImageState } from './key-image-state.component';
import { NavMenu, NavMenuProvider } from './nav-menu';
import { ReportingState } from './reporting-state.component';
import { useRouteValidation } from './use-route-validation.hook';
import { getSelectedLink } from './util';

export function ReportingLayout() {
  const [selectedLink, setSelectedLink] = useState<string>('');
  const [hasComparisonOpened, setHasComparisonOpened] = useState(false);

  const { organ } = useParams<{ organ: string }>();
  const location = useLocation();
  const isValidRoute = useRouteValidation();
  const isRqReportOrganEnabled = useIsDDFeatureFlagEnabled('FCT_RQ_REPORT_ORGAN');

  const { isOpen: isComparisonOpen } = useComparisonStore(
    useShallow((state) => ({ isOpen: state.isOpen })),
  );

  const {
    radiologistScan: { patientID },
    studyId,
  } = useStudyGuard();

  useSyncComparisonStoreWithRoute({ studyId });

  const setReportOrgans = useReportOrganStore((state) => state.setReportOrgans);
  const { batchSetStatuses, clearAllStatus, getStatus } = useReportValidationStore(
    useShallow((state) => ({
      batchSetStatuses: state.batchSetStatuses,
      clearAllStatus: state.clearAllStatus,
      getStatus: state.getStatus,
    })),
  );

  const { isLoading: isPreferencesInitLoading } = useInitializePreferences();
  const { isLoading: isReportLoading, report } = useInitializeReport();

  const { isLoading: isReportFindingByReportLoading } =
    api.fct.reportFinding.query.useGetReportFindingsByReport({
      payload: {
        params: {
          size: 50,
          user_id: ML_SERVER,
        },
        uuid: report?.uuid,
      },
    });

  const { isLoading: isHiRadiologistMedicalHistoryLoading } =
    api.hi.radiologist.query.useGetMedicalHistory({
      payload: { studyId },
    });

  const { isLoading: isHiStudyMedicalHistoryLoading } = api.hi.study.query.useGetMedicalHistory({
    payload: { studyId },
  });

  const { isLoading: isPriorStudiesLoading } = api.fct.comparison.query.useGetPriorStudies({
    payload: {
      params: {
        currentStudyId: studyId,
        patientId: patientID,
      },
    },
  });

  const { data: reportOrgans, isLoading: isFctReportOrgansLoading } =
    api.fct.reportOrgan.query.useGetReportOrgans(
      {
        payload: {
          reportUuid: report?.uuid || '',
        },
      },
      {
        enabled: Boolean(report?.uuid),
      },
    );

  const pendingPatchOrgans = useMutationState({
    filters: {
      mutationKey: [api.fct.reportOrgan.MUTATION_KEY.PATCH_REPORT_ORGAN],
      predicate: (mutation) =>
        (mutation.state.variables as PatchReportOrganRequest | undefined)?.payload?.status ===
        'published',
      status: 'pending',
    },
    select: (mutation) =>
      (mutation.state.variables as PatchReportOrganRequest | undefined)?.payload?.organ,
  });

  const isLoading =
    isPreferencesInitLoading ||
    isReportLoading ||
    isReportFindingByReportLoading ||
    isHiRadiologistMedicalHistoryLoading ||
    isHiStudyMedicalHistoryLoading ||
    isPriorStudiesLoading ||
    isFctReportOrgansLoading;

  const handleLinkSelect = useCallback((link: string) => {
    setSelectedLink(link);
  }, []);

  useEffect(() => {
    setSelectedLink(getSelectedLink(location, organ));
  }, [organ, location]);

  useEffect(() => {
    if (report?.uuid) {
      clearAllStatus();
    }
  }, [report?.uuid, clearAllStatus]);

  useEffect(() => {
    if (reportOrgans && report?.uuid) {
      if (!isRqReportOrganEnabled) {
        setReportOrgans(report.uuid, reportOrgans);
      }

      const pendingOrgans = new Set(pendingPatchOrgans);

      const statusUpdates = Object.fromEntries(
        reportOrgans.map(({ organ: organName, status }) => {
          const organStatus = getStatus(organName as ReportLinkKey);
          const isPublished = status === 'published';

          return [
            organName,
            {
              done: pendingOrgans.has(organName) ? organStatus?.done || isPublished : isPublished,
              error: organStatus?.error ?? false,
              warning: organStatus?.warning ?? false,
            },
          ];
        }),
      );

      batchSetStatuses(statusUpdates);
    }
    // pendingPatchOrgans is deliberately not a dependency: a PATCH settling on its own must not
    // re-hydrate from cached report organs that may not yet reflect the published status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportOrgans, report?.uuid]);

  // Eliminate one-render-delay for comparison panel see https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (isComparisonOpen && !hasComparisonOpened) {
    setHasComparisonOpened(true);
  }

  if (isValidRoute) {
    return (
      <AddendumProvider>
        <Stack className="h-screen" gap={0} variant="vertical">
          <Header />
          <main className="flex h-[calc(100%-5rem)] min-h-0 overflow-hidden p-4 pr-0 pb-0">
            <NavMenuProvider onLinkSelect={handleLinkSelect} selectedLink={selectedLink}>
              <NavMenu />
            </NavMenuProvider>

            <Stack className="@container min-h-0 min-w-0 flex-1">
              <Stack className="relative min-w-0 flex-1 overflow-hidden" variant="vertical">
                <Stack className="reporting-content-scroll-area min-h-0 flex-1" variant="vertical">
                  {isLoading ? (
                    <Loading />
                  ) : (
                    <KeyImageState>
                      <ReportingState />
                    </KeyImageState>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </main>

          {hasComparisonOpened ? <LazyComparisonSidePanel /> : null}

          <StudyModeDialog />
        </Stack>
      </AddendumProvider>
    );
  }

  return <Navigate to={generateAppRoute('NOT_FOUND')} />;
}
